// use-sync-ledger.ts (Story 7.6) — the Sync-Ledger READER hook.
//
// Reconcile-then-read: on each signal it runs the pure `reconcileLedger` (which upserts the
// `ledger:*` cache_metadata rows from the durable `outbound_queue`/`drafts`/prior-record state) and
// exposes the resulting `LedgerEntry[]`, newest transition first. The Submitted tab renders these.
//
// REACTIVITY — copied verbatim from `use-evaluation-conflict.ts` (the 7.5 reader-hook model, AC8
// option b "no new dependency"). The ledger's inputs are written by the main-thread drain in ANY
// open tab (the only drain since seam-74), and another tab's drain does not fire THIS tab's window
// event, so we re-reconcile on three signals:
//   1. mount (catches a status a drain changed in an earlier session or another tab — R6 §5e);
//   2. the `franklin:drain-complete` window event (a just-completed drain in THIS tab may have moved
//      an op queued → sent/server-adopted/rejected — fired by `triggerMainThreadDrain`);
//   3. tab focus / `visibilitychange → visible` (catches another tab's drain that ran while THIS
//      tab was open-but-hidden, where no local drain-complete event fired).
// `dexie-react-hooks`/`useLiveQuery` is deliberately NOT added (same decision as 7.5) — this avoids a
// second Dexie observability layer while still reflecting cross-tab writes.

import { useCallback, useEffect, useState } from "react";

import { db } from "@/lib/idb-schema";
import { readOwnerIdentity } from "@/lib/owner-scope";
import { DRAIN_COMPLETE_EVENT } from "@/lib/drain-runner";
import { reconcileLedger, type LedgerEntry } from "@/lib/sync-ledger";

export interface SyncLedgerState {
  /** Every tracked evaluation's ledger record, ordered `updated_at` DESC (newest transition first). */
  entries: LedgerEntry[];
  /** Force a re-reconcile + re-read (e.g. after navigating back to the list). */
  refresh: () => void;
}

const EMPTY: LedgerEntry[] = [];

/**
 * Read the Sync Ledger, reactively re-reconciling after a main-thread drain (this tab or another)
 * changes any evaluation's sync state. Returns an empty list until the first reconcile settles.
 */
export function useSyncLedger(): SyncLedgerState {
  const [entries, setEntries] = useState<LedgerEntry[]>(EMPTY);

  const read = useCallback(async () => {
    // The reconciler adapts the real npm-Dexie tables to its structural deps; `now` is the live clock
    // (deterministic only under test, where the lib is exercised directly).
    const next = await reconcileLedger({
      outbound_queue: db.outbound_queue,
      drafts: db.drafts,
      cache_metadata: db.cache_metadata,
      now: () => new Date().toISOString(),
      // Story 19.6 Unit E — the ledger is per-clinician on a shared device (AC19). Resolved here,
      // once per reconcile, and passed in so the pure reconciler stays free of the Dexie `meta` read.
      owner: await readOwnerIdentity(),
    });
    setEntries(next);
  }, []);

  useEffect(() => {
    void read();

    const onDrainComplete = () => void read();
    const onVisible = () => {
      if (document.visibilityState === "visible") void read();
    };

    window.addEventListener(DRAIN_COMPLETE_EVENT, onDrainComplete);
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener(DRAIN_COMPLETE_EVENT, onDrainComplete);
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [read]);

  // `read` is already `useCallback`-stable ([] deps), so return it directly — wrapping it in a fresh
  // `() => void read()` arrow each render would defeat any memo-compare a consumer runs on this object.
  return { entries, refresh: read };
}
